import type { FC } from "react";
import { useEffect, useState } from "react";
import { Autocomplete, Box, CircularProgress, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { formatProductLabel, type SelectorProduct } from "src/api/client-api/products/productSchema";

interface ProductAutocompleteProps {
    value: SelectorProduct | null;
    options: SelectorProduct[];
    onChange: (option: SelectorProduct | null) => void;
    onSearchChange: (search: string) => void;
    onSearchReset?: () => void;
    loading?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    placeholder?: string;
    noOptionsText?: string;
    sx?: object;
}

export const ProductAutocomplete: FC<ProductAutocompleteProps> = ({
    value,
    options,
    onChange,
    onSearchChange,
    onSearchReset,
    loading = false,
    disabled = false,
    error = false,
    helperText,
    placeholder,
    noOptionsText,
    sx = {},
}) => {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState("");

    useEffect(() => {
        if (value) {
            setInputValue(formatProductLabel(value));
            onSearchReset?.();
        }
    }, [value]);

    return (
        <Autocomplete
            options={options}
            value={value}
            inputValue={inputValue}
            onChange={(_, newValue) => {
                onChange(newValue);
                setInputValue(newValue ? formatProductLabel(newValue) : "");
                onSearchReset?.();
            }}
            onInputChange={(_, newInputValue, reason) => {
                if (reason === "reset") {
                    setInputValue(newInputValue);
                    return;
                }

                setInputValue(newInputValue);
                if (reason === "input") {
                    onSearchChange(newInputValue);
                }
            }}
            getOptionLabel={(option) => formatProductLabel(option)}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            loading={loading}
            disabled={disabled}
            filterOptions={(items) => items}
            noOptionsText={
                noOptionsText ??
                (loading ?
                    t("product.select.loading", "Loading...")
                :   t("product.select.noOptions", "No products available"))
            }
            loadingText={t("product.select.loading", "Loading...")}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                    <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {formatProductLabel(option)}
                    </Typography>
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={placeholder ?? t("product.select.placeholder", "Select a product")}
                    variant="outlined"
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ?
                                    <CircularProgress color="inherit" size={20} />
                                :   null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "24px",
                            backgroundColor: "#FFFFFF",
                            minHeight: "40px",
                            "& fieldset": {
                                borderColor: "#E7E6E4",
                            },
                            "&:hover fieldset": {
                                borderColor: "#D0D0D0",
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: "primary.main",
                            },
                        },
                        "& .MuiOutlinedInput-input": {
                            fontSize: "0.875rem",
                            padding: "0px 16px !important",
                            color: "#393A36",
                            "&::placeholder": {
                                color: "#A3A2A0",
                                opacity: 1,
                            },
                        },
                        ...sx,
                    }}
                />
            )}
        />
    );
};
